import { useEffect, useState } from "react";

/**
 * A screen with no room to spare: a phone in either orientation, and a laptop
 * window squeezed down to the same shape.
 *
 * Width alone is not enough. A phone in landscape is 844px wide — wider than
 * the `sm` breakpoint the room's styles key off — and 390px tall, which is
 * less vertical room than a portrait phone has. The bar and the header are
 * what eat that height, so the height is what has to be asked about.
 */
export const COMPACT_ROOM_QUERY = "(max-width: 640px), (max-height: 500px)";

/**
 * Whether the room should draw itself for a small screen.
 *
 * A media query rather than the app's `useBreakpoint`: that hook listens to
 * `resize` and reports a Tailwind step, and neither answers the question this
 * one asks. `matchMedia` also fires on an orientation change that does not
 * resize the window, which on a phone is exactly when the answer changes.
 *
 * Guarded throughout: `matchMedia` is missing in jsdom's older shims and in
 * the odd embedded webview, and a room that will not render is a worse
 * failure than a room drawn for a laptop.
 */
export function useCompactRoom(): boolean {
    const [compact, setCompact] = useState(() => matches());

    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return;
        }

        const query = window.matchMedia(COMPACT_ROOM_QUERY);
        const onChange = () => setCompact(query.matches);
        onChange();

        // `addListener` is the deprecated half of the pair, kept for the
        // Safari versions that never shipped the other one — which is a real
        // share of the phones a patient joins from.
        if (typeof query.addEventListener === "function") {
            query.addEventListener("change", onChange);
            return () => query.removeEventListener("change", onChange);
        }
        query.addListener(onChange);
        return () => query.removeListener(onChange);
    }, []);

    return compact;
}

function matches(): boolean {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return false;
    }
    return window.matchMedia(COMPACT_ROOM_QUERY).matches;
}
